import { GetRecurringPaymentsRequest, RecurringPaymentsContent } from 'supporter';
import { makeFastFuncOfGetI18n } from '@/utils/i18n';
import {addMonth, getToday} from '@/utils/dateUtils';
// import {QueryObject} from '@/components/order/detail/orderRecurringPayments/SearchForm.vue';

type QueryObject = Omit<GetRecurringPaymentsRequest['params'], 'page' | 'size'>

export interface GridDataItem extends Omit<RecurringPaymentsContent,	'deliveryCycleType' | 'deliveryCycle' | 'deliveryDate' | 'round' | 'closeReason' | 'closeReason'> {
	deliveryDateAndRound: {
		date: number,
		round: number,
	};
	closingInfo: {
		reason: string;
		reasonDetail: string;
	}
}

export interface GridProps {
	columns: (
		{
			name: string,
			header: any,
			minWidth: number,
			align: string
			formatter?: (row: any) => any,
		}	|
		{
			name: string,
			width: number,
			header: any,
			align: string,
			formatter?: (row: any) => any,
		}
		)[],
	options: {
		rowHeaders: string[],
		columnOptions: {
			resizable: boolean,
			minWidth: number
		},
		pageSize: string;
		pageOptions: {
			perPage: number;
			page: number;
			totalCount: number;
			centerAlign: boolean;
		},
		haveExcelDownBtn: boolean;
	},
	header: {
		height: number
	}
}

export const getI18nOfThis = makeFastFuncOfGetI18n('RECURRINGPAYMENTS_ORDER');

export const gridProps = (): GridProps => ({
	header: {
		height: 34,
	},
	columns: [
		{
			header: getI18nOfThis('RECURRING_PAYMENT_NO'),
			name: 'recurringPaymentNo',
			width: 130,
			align: 'center',
		},
		{
			header: window.$t('COMMON_MALL'),
			name: 'mallName',
			align: 'center',
			minWidth: 93,
		},
		{
			header: getI18nOfThis('REGISTER_YMD'),
			name: 'registerYmd',
			width: 130,
			align: 'center',
		},
		{
			header: window.$t('COMMON_ID'),
			name: 'memberId',
			width: 80,
			align: 'center',
		},
		{
			header: getI18nOfThis('REGISTRANT_NAME'),
			name: 'memberName',
			width: 80,
			align: 'center',
		},
		{
			header: window.$t('COMMON_PRODUCT_NO'),
			name: 'productNo',
			width: 130,
			align: 'center',
			formatter: ({row}: any) => `
				<button 
					type="button" 
					data-mall-no=${row.mallNo} 
					data-product-no=${row.productNo}
					name="recurringPaymentProductNoBtn"
				>
					${row.productNo}
				</button>
			`
		},
		{
			header: window.$t('COMMON_PRODUCT_NAME'),
			name: 'productName',
			width: 185,
			align: 'left',
		},
		{
			header: getI18nOfThis('SELLING_PRICE'),
			name: 'standardAmt',
			width: 113,
			align: 'center',
			formatter: ({ row }: any) => row.standardAmt.toLocaleString(),
		},
		{
			header: getI18nOfThis('DISCOUNT_ON_RECURRING_PAYMENTS_IMMEDIATELY'),
			name: 'discountAmt',
			width: 140,
			align: 'center',
			formatter: ({row}: any) => row.discountAmt.toLocaleString(),
			// 정기결제 주문 관리 페이지에서는 '정기결제(배송) 즉시할인' 값이 원 단위로만 표기됨
		},
		{
			header: getI18nOfThis('DISCOUNT_PRICE'),
			name: 'buyAmt',
			width: 115,
			align: 'center',
			formatter: ({ row }: any) => row.buyAmt.toLocaleString(),
		},
		{
			header: getI18nOfThis('DELIVERY_CYCLE'),
			name: 'deliveryCycle',
			width: 115,
			align: 'center',
			formatter: ({ row }: any) => row.deliveryCycle.toString() + window.$t('COMMON_MONTHS'),
		},
		{
			header: getI18nOfThis('DELIVERY_DATE_AND_ROUND'),
			name: 'deliveryDateAndRound',
			width: 115,
			align: 'center',
			formatter: ({ row }: any) => row.deliveryDate + window.$t('COMMON_DAY') + ' '
				+ `(${row.round + getI18nOfThis('DELIVERY_ROUND')})`,
		},
		{
			header: getI18nOfThis('RECURRING_PAYMENT_STATUS'),
			name: 'recurringPaymentStatusLabel',
			width: 115,
			align: 'center',
			formatter: ({ row }: any) => row.recurringPaymentStatusLabel,
		},
		{
			header: getI18nOfThis('CLOSE_YMD'),
			name: 'closeYmd',
			width: 115,
			align: 'center',
			formatter: ({ row }: any) => row.closeYmd ?? '-',
		},
		{
			header: getI18nOfThis('CLOSE_INFO'),
			name: 'closeInfo',
			width: 115,
			align: 'center',
			formatter: ({ row }: any) => {
				const { closeReason, closeReasonDetail } = row;
				if (closeReason) {
					const $btn = document.createElement('button');
					$btn.dataset.closeReason = closeReason;
					$btn.dataset.closeReasonDetail = closeReasonDetail ?? '-';
					$btn.className = 'order-recurring-payments__close-info-btn'
					$btn.innerText = window.$t('COMMON_VIEW');
					return $btn.outerHTML;
				}
				return '-';
			}
		},
	],
	options: {
		columnOptions: {
			minWidth: 50,
			resizable: true
		},
		rowHeaders: ['checkbox'],
		pageSize: '100',
		pageOptions: {
			perPage: 100,
			page: 1,
			totalCount: 0,
			centerAlign: true
		},
		haveExcelDownBtn: false,
	}
})

export const makeDefaultSearchQueryObject = (defaultMallNos: number[]): Readonly<QueryObject> => ({
	mallNos: defaultMallNos,
	searchType: 'PRODUCT_NAME',
	startYmd: addMonth(new Date(), -3),
	endYmd: getToday(),
	deliveryCycles: [1, 2, 3, 4, 5, 6] as unknown as string[],
})
